import { apiClient } from "module/api";
import { authorization } from "module/auth";

/**
 * Lightweight batched resolver for user display info, backed by our own
 * `/platform/users/resolve` endpoint.
 *
 * Why not just use Liveblocks' `useUser` + `resolveUsers`? Because Liveblocks
 * caches user info server-side from whatever `userInfo` was last sent via the
 * auth endpoint. If an admin ever connected with a stale/wrong presence name,
 * that value is pinned for every consumer until the admin reconnects with
 * fresh auth. Our own DB is the source of truth, so we bypass that cache.
 *
 * Strategy:
 *  - In-memory cache keyed by Liveblocks user id ("user-123").
 *  - Pending requests are batched with a short micro-debounce (30ms) so that
 *    rendering a thread with N comments only triggers one HTTP round-trip.
 *  - Subscribers re-render via a simple listener list.
 */

export type ResolvedUserInfo = {
  id: string;
  name: string;
  avatar?: string | null;
};

type Listener = () => void;

const cache = new Map<string, ResolvedUserInfo | null>(); // null = resolved-as-missing
const inFlight = new Set<string>();
const pending = new Set<string>();
const listeners = new Map<string, Set<Listener>>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

// Track the authenticated user that owns the current cache contents. A
// logout→login as a different user without a page refresh would otherwise
// serve the previous session's resolved profile data (same-ID collisions
// across tenants — Oppizi internal staff seen in both Company A and B
// designs — would read stale names/avatars). We check this on every read
// and every new subscription; if the owner changed, the cache is wiped
// and in-flight work is cancelled so the new session re-resolves from DB.
let cacheOwnerId: string | number | null = null;

const getCurrentOwnerId = (): string | number | null => {
  try {
    return authorization.getUser?.()?.id ?? null;
  } catch {
    return null;
  }
};

const ensureOwner = (): void => {
  const current = getCurrentOwnerId();
  if (current === cacheOwnerId) return;
  // Owner changed — drop everything. Notify outstanding listeners so any
  // mounted UI re-subscribes and re-queues a fetch under the new owner.
  cache.clear();
  inFlight.clear();
  pending.clear();
  if (flushTimer != null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  const toNotify = Array.from(listeners.keys());
  cacheOwnerId = current;
  toNotify.forEach(notify);
};

const notify = (id: string) => {
  const set = listeners.get(id);
  if (!set) return;
  set.forEach((l) => {
    try {
      l();
    } catch {
      /* ignore */
    }
  });
};

const flush = async () => {
  flushTimer = null;
  const ids = Array.from(pending);
  pending.clear();
  if (ids.length === 0) return;
  ids.forEach((id) => inFlight.add(id));
  try {
    const rows = await apiClient.resolveUsersBatch({ ids });
    const byId = new Map<string, ResolvedUserInfo>();
    (rows || []).forEach((r: any) => {
      if (r?.id) byId.set(r.id, { id: r.id, name: r.name, avatar: r.avatar });
    });
    ids.forEach((id) => {
      cache.set(id, byId.get(id) ?? null);
      inFlight.delete(id);
      notify(id);
    });
  } catch {
    ids.forEach((id) => {
      // Leave uncached on error so we retry next time
      inFlight.delete(id);
      notify(id);
    });
  }
};

const queue = (id: string) => {
  if (cache.has(id) || inFlight.has(id) || pending.has(id)) return;
  pending.add(id);
  if (flushTimer == null) {
    flushTimer = setTimeout(flush, 30);
  }
};

export const getCachedUser = (id: string): ResolvedUserInfo | null | undefined => {
  ensureOwner();
  return cache.get(id);
};

export const subscribeUser = (id: string, listener: Listener): (() => void) => {
  ensureOwner();
  if (!listeners.has(id)) listeners.set(id, new Set());
  listeners.get(id)!.add(listener);
  queue(id);
  return () => {
    const set = listeners.get(id);
    if (!set) return;
    set.delete(listener);
    if (set.size === 0) listeners.delete(id);
  };
};

/**
 * Invalidate a specific user (or all users) so the next subscribe re-fetches.
 * Useful after a user updates their profile.
 */
export const invalidateUser = (id?: string) => {
  if (id) {
    cache.delete(id);
  } else {
    cache.clear();
  }
};
